import { isArray, isEqual, isNil, isObject, toPairs } from 'lodash-es';

import type {
  MetadataFieldValue,
  MetadataFieldValuesPatched,
  MetadataValue,
  MetadataValues,
  ModeType,
} from '@iconik-io/iconik-api/src/schema_patched/metadata';

const MODE_TYPES: ModeType[] = ['append', 'delete', 'overwrite'];

export function isModeType(value: MetadataValue): value is ModeType {
  return MODE_TYPES.some((mode) => mode === value);
}

// Field name prefix for the bulk update mode
export const FIELD_UPDATE_MODE_PREFIX = '__iconik_field_update_mode__';

// Field name prefix for the marker field, which is a checkbox and used to mark
// metadata fields to update in bulk mode
export const FIELD_UPDATE_BULK_PREFIX = '__iconik_field_update_bulk__';

export const pickBulkMarkedFields = (data: MetadataValues): MetadataValues => {
  const result: MetadataValues = {};
  Object.entries(data).forEach(([key, value]) => {
    if (key.startsWith(FIELD_UPDATE_MODE_PREFIX)) {
      result[key] = value;
    } else if (key.startsWith(FIELD_UPDATE_BULK_PREFIX) && value) {
      const originalKey = key.replace(FIELD_UPDATE_BULK_PREFIX, '');
      result[originalKey] = data[originalKey];
    }
  });
  return result;
};

type MetadataFieldValueUpdate = {
  field_values?: {
    [k: string]: string | number | boolean;
  }[];
  mode?: ModeType;
};

type MetadataBulkUpdate = {
  [k: string]: MetadataFieldValueUpdate;
};

/**
 * Takes the server format used for bulk updates, converts it to the format used in the
 * form by flattening the field_values array and adds the bulk prefixes to mark those
 * fields that have been set with the checkbox that they will update
 *
 * @param data - The server format for bulk updates
 * @returns An object that can be passed in to the metadata form in bulk update mode
 */
export const serverBulkUpdateToForm = (
  data: MetadataBulkUpdate,
): MetadataValues => {
  const result: MetadataValues = {};
  Object.entries(data).forEach(([key, entry]) => {
    const fieldValues = entry.field_values;
    if (fieldValues) {
      result[key] = fieldValues.map((v) => ({ value: String(v.value) }));
      result[FIELD_UPDATE_BULK_PREFIX + key] = 'checked';
    }
    if (entry.mode) {
      result[FIELD_UPDATE_MODE_PREFIX + key] = entry.mode;
    }
  });

  return result;
};

export const formToServerFormat = (
  currentValues: MetadataValues = {},
  data: MetadataValues = {},
): MetadataFieldValuesPatched => {
  const newData: MetadataFieldValuesPatched = {};
  let fieldValue;
  Object.entries(data).forEach(([key, value]) => {
    fieldValue = value;
    if (isNil(value)) {
      fieldValue = [];
    } else if (isArray(fieldValue)) {
      if (fieldValue !== undefined) {
        fieldValue = fieldValue.map((v) => {
          const newVal = isObject(v) ? v : { value: v };
          return newVal;
        });
      }
    } else if (!isArray(fieldValue)) {
      fieldValue =
        fieldValue !== undefined ? [{ value: String(fieldValue) }] : undefined;
    }
    if (
      !isEqual(fieldValue, currentValues[key]) &&
      !key.startsWith(FIELD_UPDATE_MODE_PREFIX)
    ) {
      newData[key] = {
        field_values: fieldValue,
      };
      const modeValue = data[FIELD_UPDATE_MODE_PREFIX + key];
      if (isModeType(modeValue)) {
        newData[key].mode = modeValue;
      }
    }
  });
  return newData;
};

type AppStateMetadataFieldValues = Record<
  string,
  MetadataFieldValue[] | undefined
>;

export const serverToReduxFormat = (
  metadataValues: MetadataFieldValuesPatched,
): AppStateMetadataFieldValues => {
  const valuesDict: AppStateMetadataFieldValues = {};
  toPairs(metadataValues).forEach(([key, value]) => {
    valuesDict[key] = value.field_values;
  });
  return valuesDict;
};
